import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, QrCode, Nfc, Trash2, Pencil, Download, Printer, Armchair, Store as StoreIcon, ExternalLink } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { feedbackHref } from "../../lib/feedbackLink";
import { Label, EmptyState, InfoBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const TYPE_ICON = { table: Armchair, counter: StoreIcon };

function AddDialog({ open, onClose, locations, defaultLoc, onDone }) {
  const [mode, setMode] = useState("bulk");
  const [f, setF] = useState({ location_id: defaultLoc || "", type: "table", name: "", prefix: "Table", start: 1, end: 10 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ location_id: defaultLoc || locations[0]?.id || "", type: "table", name: "", prefix: "Table", start: 1, end: 10 }); }, [open, defaultLoc, locations]);
  const set = (k) => (v) => setF((c) => ({ ...c, [k]: v, ...(k === "type" ? { prefix: v === "table" ? "Table" : "Counter" } : {}) }));
  const save = async () => {
    setBusy(true);
    try {
      if (mode === "single") { await api.post("/points", { location_id: f.location_id, type: f.type, name: f.name }); toast.success(`${f.name} created`); }
      else { const { data } = await api.post("/points/bulk", { location_id: f.location_id, type: f.type, prefix: f.prefix, start: +f.start, end: +f.end }); toast.success(`${data.created} created${data.skipped ? `, ${data.skipped} already existed` : ""}`); }
      onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="point-add-dialog">
        <DialogHeader><DialogTitle>Add counters or tables</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">{[["bulk", "Several at once"], ["single", "Just one"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`point-mode-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${mode === k ? "bg-[#4338CA] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>))}</div>
          <Select value={f.location_id} onValueChange={set("location_id")}>
            <SelectTrigger data-testid="point-location"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.type} onValueChange={set("type")}>
            <SelectTrigger data-testid="point-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="table">Table (waiter service)</SelectItem><SelectItem value="counter">Counter / till</SelectItem></SelectContent>
          </Select>
          {mode === "single" ? <Input value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder={f.type === "table" ? "e.g. Table 4 or Patio 2" : "e.g. Counter 1"} data-testid="point-name" /> : (
            <div className="grid grid-cols-[1fr_80px_80px] gap-2">
              <Input value={f.prefix} onChange={(e) => set("prefix")(e.target.value)} placeholder="Name" data-testid="point-prefix" />
              <Input type="number" value={f.start} onChange={(e) => set("start")(e.target.value)} data-testid="point-start" />
              <Input type="number" value={f.end} onChange={(e) => set("end")(e.target.value)} data-testid="point-end" />
              <p className="col-span-3 text-xs text-slate-400">Creates "{f.prefix} {f.start}" to "{f.prefix} {f.end}" (max 100 at a time). Existing names are skipped.</p>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} disabled={busy || !f.location_id || (mode === "single" && !f.name.trim())} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="point-save">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QrDialog({ point, onClose, onNfc, onTent }) {
  const [qr, setQr] = useState(null);
  useEffect(() => { setQr(null); if (point) api.get(`/points/${point.id}/qr`).then((r) => setQr(r.data)).catch((e) => toast.error(apiError(e))); }, [point]);
  return (
    <Dialog open={!!point} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="point-qr-dialog">
        <DialogHeader><DialogTitle>{point?.name} · {point?.location_name}</DialogTitle></DialogHeader>
        {!qr ? <p className="py-10 text-center text-slate-400">Loading…</p> : (
          <div className="text-center">
            <img src={qr.png} alt={`QR code for ${qr.name}`} className="mx-auto w-56" data-testid="point-qr-img" />
            <p className="mt-2 break-all text-xs text-slate-400">{qr.url}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <a href={qr.png} download={`${qr.name.replace(/\s+/g, "-")}-qr.png`}><Button variant="outline" size="sm" data-testid="point-qr-download"><Download className="mr-1 h-4 w-4" />PNG</Button></a>
              <Button variant="outline" size="sm" onClick={() => onTent(point)} disabled={!point?.active} title={point?.active ? "" : "Activate this point to print"} data-testid="point-qr-print"><Printer className="mr-1 h-4 w-4" />Print tent</Button>
              <Button variant="outline" size="sm" onClick={() => onNfc(point, qr.url)} data-testid="point-nfc-write"><Nfc className="mr-1 h-4 w-4" />Write NFC tag</Button>
              <a href={qr.url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid="point-open"><ExternalLink className="mr-1 h-4 w-4" />Open</Button></a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DEFAULT_CTA = "Rate or Recognise Staff in < 20 Seconds!";
const DEFAULT_TENT = { size: "a6", theme: "light", cta: DEFAULT_CTA, show_org_name: true, font_family: "sans", org_name_size: "medium" };

function Pills({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-2">{options.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${value === k ? "bg-[#4338CA] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>))}</div>
  );
}

function TentPreview({ open, f, scope, label }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !f.cta.trim()) { return undefined; }
    let url; let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.post("/points/tents/preview.png", { ...f, ...scope }, { responseType: "blob" })
        .then(({ data }) => { if (live) { url = URL.createObjectURL(data); setSrc((old) => { if (old) URL.revokeObjectURL(old); return url; }); } })
        .catch(() => {}).finally(() => { if (live) setLoading(false); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [open, f, scope]);
  useEffect(() => { if (!open) { setSrc(null); } }, [open]);
  return (
    <div className="relative flex min-h-[260px] items-center justify-center rounded-xl bg-slate-100 p-4 pt-9" data-testid="tent-preview">
      {src ? <img src={src} alt="Table tent preview" className={`max-h-[420px] w-auto rounded-sm shadow-lg ring-1 ring-black/5 transition-opacity duration-200 ${loading ? "opacity-60" : "opacity-100"}`} data-testid="tent-preview-img" />
        : <p className="text-xs text-slate-400">Loading preview…</p>}
      <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Front · {label}</span>
    </div>
  );
}

function TentDialog({ open, onClose, loc, count, point }) {
  const [f, setF] = useState(DEFAULT_TENT);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/points/tents/defaults").then((r) => setF({ ...DEFAULT_TENT, ...r.data })).catch(() => setF(DEFAULT_TENT));
  }, [open]);
  const scope = useMemo(() => (point ? { point_ids: [point.id] } : { location_id: loc !== "all" ? loc : null }), [point, loc]);
  const n = point ? 1 : count;
  const set = (k) => (v) => setF((c) => ({ ...c, [k]: v }));
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/points/tents.pdf", { ...f, ...scope }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = point ? `${point.name.replace(/\s+/g, "-")}-tent.pdf` : "table-tents.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Table tents ready — your preferences are saved"); onClose();
    } catch (e) { toast.error(e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="tent-dialog">
        <DialogHeader><DialogTitle>{point ? `Print tent · ${point.name}` : "Print table tents"}</DialogTitle></DialogHeader>
        <div className="grid gap-5 md:grid-cols-[1fr_300px]">
        <div className="order-2 space-y-4 text-sm md:order-1">
          <p className="text-slate-500" data-testid="tent-count">{point ? `A fold-over tent for ${point.name} (${point.location_name}), with your logo, its name and its QR code.` : `One fold-over tent for each of the ${count} active counters/tables${loc !== "all" ? " at this location" : ""}, with your logo, the table name and its QR code.`}</p>
          <div><p className="mb-1.5 text-xs font-bold uppercase text-slate-400">Size</p>
            <Pills value={f.size} onChange={set("size")} testid="tent-size" options={[["a6", "A6 · 2 per A4"], ["a5", "A5 · front & back per A4"]]} /></div>
          <div><p className="mb-1.5 text-xs font-bold uppercase text-slate-400">Style</p>
            <Pills value={f.theme} onChange={set("theme")} testid="tent-theme" options={[["light", "Clean white (low ink)"], ["dark", "Brand dark"]]} /></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <label className="flex cursor-pointer items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase text-slate-500">Show organisation name in header</span>
              <Switch checked={f.show_org_name} onCheckedChange={set("show_org_name")} data-testid="tent-show-org" />
            </label>
            {f.show_org_name && (
              <div className="mt-3 space-y-3">
                <div><p className="mb-1.5 text-xs font-bold uppercase text-slate-400">Font</p>
                  <Pills value={f.font_family} onChange={set("font_family")} testid="tent-font" options={[["sans", "Sans"], ["serif", "Serif"], ["display", "Display"]]} /></div>
                <div><p className="mb-1.5 text-xs font-bold uppercase text-slate-400">Text size</p>
                  <Pills value={f.org_name_size} onChange={set("org_name_size")} testid="tent-orgsize" options={[["small", "Small"], ["medium", "Medium"], ["large", "Large"]]} /></div>
              </div>
            )}
          </div>
          <div><p className="mb-1.5 text-xs font-bold uppercase text-slate-400">Message to customers</p>
            <Input value={f.cta} maxLength={80} onChange={(e) => set("cta")(e.target.value)} data-testid="tent-cta" /></div>
          <p className="text-xs text-slate-400">Your choices here are saved as the new default for next time. Print at 100% scale and cut along the dotted line. A6: fold on the dashed line to stand. A5: slide both halves into an A5 acrylic stand. Stick an NFC tag on the card for tap-to-rate.</p>
        </div>
        <div className="order-1 md:order-2"><TentPreview open={open && n > 0} f={f} scope={scope} label={point ? point.name : "first table"} /></div>
        </div>
        <DialogFooter><Button onClick={go} disabled={busy || !n || !f.cta.trim()} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="tent-generate"><Printer className="mr-1 h-4 w-4" />{busy ? "Building PDF…" : "Download PDF"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PointRow({ p, onToggle, onQr, onTent, onRename, onDelete }) {
  const Icon = TYPE_ICON[p.type];
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`point-row-${p.id}`}>
      <td className="px-4 py-3"><span className="flex items-center gap-2 font-semibold text-slate-800"><Icon className="h-4 w-4 text-indigo-500" />{p.name}
        {p.nfc_provisioned && <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-600">NFC</span>}</span></td>
      <td className="capitalize text-slate-600">{p.type}</td>
      <td className="text-slate-600">{p.location_name}</td>
      <td className="text-slate-600">{p.scan_count}</td>
      <td><Link to={feedbackHref({ point_id: p.id })} className="font-semibold text-indigo-700 hover:underline" data-testid={`point-ratings-${p.id}`}>{p.ratings}</Link>
        <span className="ml-1 text-xs text-slate-400">({p.ratings_30d} in 30d)</span></td>
      <td className="text-slate-600">{p.avg_rating ?? "—"}</td>
      <td><Switch checked={p.active} onCheckedChange={(v) => onToggle(p, v)} data-testid={`point-active-${p.id}`} /></td>
      <td className="whitespace-nowrap pr-3 text-right">
        <Button size="sm" variant="outline" onClick={() => onQr(p)} data-testid={`point-qr-${p.id}`}><QrCode className="mr-1 h-4 w-4" />QR / NFC</Button>
        <Button size="sm" variant="outline" className="ml-1.5" onClick={() => onTent(p)} disabled={!p.active} title={p.active ? "Print table tent" : "Activate to print"} data-testid={`point-tent-${p.id}`}><Printer className="mr-1 h-4 w-4" />Tent</Button>
        <Button size="sm" variant="ghost" onClick={() => onRename(p)} aria-label="Rename" data-testid={`point-rename-${p.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(p)} aria-label="Delete" data-testid={`point-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
      </td>
    </tr>
  );
}

export default function ServicePoints() {
  const [rows, setRows] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("all");
  const [adding, setAdding] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [tents, setTents] = useState(null);

  const load = useCallback(() => {
    api.get(`/points${loc !== "all" ? `?location_id=${loc}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [loc]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);

  const toggle = async (p, active) => { try { await api.patch(`/points/${p.id}`, { active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const rename = async (p) => {
    const name = window.prompt("New name", p.name);
    if (!name || name === p.name) return;
    try { await api.patch(`/points/${p.id}`, { name }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Its QR code and NFC tag will stop working. Past ratings are kept.`)) return;
    try { await api.delete(`/points/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const writeNfc = async (p, url) => {
    if (!("NDEFReader" in window)) { toast.error("NFC writing needs Chrome on an Android phone. Use the QR code instead."); return; }
    try {
      // eslint-disable-next-line no-undef
      await new NDEFReader().write({ records: [{ recordType: "url", data: `${url}${url.includes("?") ? "&" : "?"}s=nfc` }] });
      await api.post(`/points/${p.id}/nfc-programmed`);
      toast.success("NFC tag programmed"); load();
    } catch { toast.error("NFC write failed or was cancelled."); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><span className="flex items-center gap-1.5"><Label>People</Label>
          <InfoBadge title="Counters & Tables" testId="points-info" text="Shared QR codes and NFC tags for a counter, till or table. Customers scan, pick who served them from the staff at that location, and rate them as normal. Useful when staff are busy with the next customer, or for waiter service." /></span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Counters & Tables</h1></div>
        <div className="flex flex-wrap gap-2">
          <Select value={loc} onValueChange={setLoc}>
            <SelectTrigger className="w-48" data-testid="points-location-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All locations</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setTents("all")} disabled={!rows?.some((p) => p.active)} data-testid="points-print-tents"><Printer className="mr-1 h-4 w-4" />Print table tents</Button>
          <Button onClick={() => setAdding(true)} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="points-add"><Plus className="mr-1 h-4 w-4" />Add counters / tables</Button>
        </div>
      </div>
      {!rows ? <p className="text-slate-400">Loading…</p> : rows.length === 0 ? <EmptyState icon={QrCode} title="No counters or tables yet" hint="Add tables or counters to print shared rating QR codes." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[860px] text-sm" data-testid="points-table">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-3">Name</th><th>Type</th><th>Location</th><th>Scans</th><th>Ratings</th><th>Avg</th><th>Active</th><th /></tr></thead>
            <tbody>{rows.map((p) => <PointRow key={p.id} p={p} onToggle={toggle} onQr={setQrFor} onTent={setTents} onRename={rename} onDelete={remove} />)}</tbody>
          </table>
        </div>
      )}
      <AddDialog open={adding} onClose={() => setAdding(false)} locations={locations} defaultLoc={loc !== "all" ? loc : ""} onDone={load} />
      <TentDialog open={!!tents} onClose={() => setTents(null)} loc={loc} point={tents && tents !== "all" ? tents : null} count={(rows || []).filter((p) => p.active).length} />
      <QrDialog point={qrFor} onClose={() => setQrFor(null)} onNfc={writeNfc} onTent={(p) => { setQrFor(null); setTents(p); }} />
    </div>
  );
}
